import { decimalNegate, decimalSum, isDecimal } from '@flighter/methods/accounting/decimal';
import { TaxType } from '@flighter/types/models/TaxType';

/**
 * Display helpers for tax rates (plan 001 A14). Rates are the decimal
 * strings the backend sends (`13.000000`, `9.975`); no float maths.
 */

type RatedTax = {
	rate: string | number | null | undefined;
	type: TaxType | `${TaxType}` | string;
	is_group?: boolean;
	effective_rate?: string | null;
};

/** `13.000000` → `13`, `9.975000` → `9.975`, `.5` → `0.5`, padded to at least `minScale` decimals. */
export function trimTaxRate(rate: string | number | null | undefined, minScale = 0): string {
	let text = isDecimal(rate) ? decimalSum([rate]) : '0';
	if (text.includes('.')) text = text.replace(/0+$/, '').replace(/\.$/, '');
	if (text === '' || text === '-0') text = '0';
	if (minScale <= 0) return text;

	const [whole, fraction = ''] = text.split('.');

	return `${whole}.${fraction.padEnd(minScale, '0')}`;
}

/**
 * The combined percentage of a group's members, like TaxModel::effective_rate:
 * withholding members count negative, fixed (per unit) members not at all.
 */
export function sumTaxRates(members: Array<Pick<RatedTax, 'rate' | 'type'>>): string {
	return trimTaxRate(decimalSum(members
		.filter((member) => member.type !== TaxType.Fixed)
		.map((member) => (member.type === TaxType.Withholding ? decimalNegate(member.rate) : member.rate))));
}

/**
 * A tax's rate for a badge or a cell: `13%`, `0.10 per unit` (Fixed; the
 * caller passes the translated "per unit"), or a group's combined `14.975%`.
 */
export function taxRateLabel(tax: RatedTax, perUnit = 'per unit'): string {
	if (tax.is_group) return `${trimTaxRate(tax.effective_rate ?? '0')}%`;
	if (tax.type === TaxType.Fixed) return `${trimTaxRate(tax.rate, 2)} ${perUnit}`.trim();

	return `${trimTaxRate(tax.rate)}%`;
}
